// src/components/PoseVisualization.js
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config.js';

const PoseVisualization = ({ poseJsonPath, videoUrl }) => {
  const canvasRef = useRef(null);
  const [poseData, setPoseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPoseData = async () => {
      if (!poseJsonPath) {
        console.warn('[PoseViz] ⚠️ No poseJsonPath provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Extract filename from full path
        const filename = poseJsonPath.includes('/') 
          ? poseJsonPath.split('/').pop() 
          : poseJsonPath;

        const url = `${API_BASE_URL}/features/${filename}`;
        
        console.log('[PoseViz] 🔍 Input poseJsonPath:', poseJsonPath);
        console.log('[PoseViz] 🔍 Extracted filename:', filename);
        console.log('[PoseViz] 🔍 API_BASE_URL:', API_BASE_URL);
        console.log('[PoseViz] 🔍 Fetch URL:', url);
        
        // Construct proper URL to backend
        const response = await fetch(url);
        
        console.log('[PoseViz] 🔍 Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText} - File may not exist or be accessible`);
        }
        
        const text = await response.text();
        console.log('[PoseViz] 🔍 Response text (first 200 chars):', text.substring(0, 200));
        
        const data = JSON.parse(text);
        console.log('[PoseViz] ✅ Successfully parsed JSON');
        console.log('[PoseViz] 🔍 Landmarks count:', data.landmarks?.length);
        
        setPoseData(data.landmarks || []);
      } catch (err) {
        console.error('[PoseViz] ❌ Error:', err.message);
        console.error('[PoseViz] ❌ Error type:', err.constructor.name);
        console.error('[PoseViz] ❌ Full error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPoseData();
  }, [poseJsonPath]);

  // Draw skeleton on canvas
  useEffect(() => {
    if (!canvasRef.current || !poseData || poseData.length === 0) return;

    // MediaPipe landmark connections (skeleton)
    const CONNECTIONS = [
      [11, 12], // shoulders
      [11, 13], [13, 15], // left arm
      [12, 14], [14, 16], // right arm
      [11, 23], [12, 24], // torso to hips
      [23, 24], // hip connection
      [23, 25], [25, 27], // left leg
      [24, 26], [26, 28], // right leg
    ];

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let frameIndex = 0;
    let animationId = null;
    let skipCounter = 0;
    const FRAME_SKIP = 10; // Slow down by 10x

    // Set canvas size
    canvas.width = 640;
    canvas.height = 480;

    // Helper function to draw pose skeleton
    const drawPoseSkeleton = (landmarks) => {
      if (!landmarks || landmarks.length === 0) return;

      // Draw connections (skeleton lines)
      ctx.strokeStyle = '#00FF00';
      ctx.lineWidth = 2;
      CONNECTIONS.forEach(([start, end]) => {
        if (landmarks[start] && landmarks[end]) {
          const [x1, y1] = [landmarks[start][0] * canvas.width, landmarks[start][1] * canvas.height];
          const [x2, y2] = [landmarks[end][0] * canvas.width, landmarks[end][1] * canvas.height];

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      });

      // Draw landmarks (circles)
      ctx.fillStyle = '#FF0000';
      landmarks.forEach((lm) => {
        if (lm && lm[3] > 0.5) { // visibility check
          const x = lm[0] * canvas.width;
          const y = lm[1] * canvas.height;
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fill();
        }
      });
    };

    // Animation loop to play through all frames
    const animate = () => {
      if (!canvas || !ctx) return;

      // Draw background
      ctx.fillStyle = '#222';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw current frame's skeleton
      if (poseData[frameIndex]) {
        drawPoseSkeleton(poseData[frameIndex]);
      }

      // Frame counter
      ctx.fillStyle = '#00FF00';
      ctx.font = '12px monospace';
      ctx.fillText(`Frame: ${frameIndex + 1}/${poseData.length}`, 10, 20);

      // Only advance frame every FRAME_SKIP calls (slow motion effect)
      skipCounter++;
      if (skipCounter >= FRAME_SKIP) {
        frameIndex = (frameIndex + 1) % poseData.length;
        skipCounter = 0;
      }

      animationId = requestAnimationFrame(animate);
    };

    // Start animation
    animationId = requestAnimationFrame(animate);

    // Cleanup animation on unmount
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [poseData]);

  if (loading) return <div style={{ fontSize: 12, color: '#999', padding: '1rem' }}>⏳ Loading pose data...</div>;
  if (error) return (
    <div style={{ fontSize: 12, color: '#c33', padding: '1rem', background: '#fee', borderRadius: '4px' }}>
      <strong>❌ Error loading pose data:</strong> {error}
      <div style={{ fontSize: 10, color: '#999', marginTop: '0.5rem' }}>
        💡 Check browser console (F12) for details. See TROUBLESHOOT_POSE_ERROR.md for help.
      </div>
    </div>
  );
  if (!poseData || poseData.length === 0) return <div style={{ fontSize: 12, color: '#999', padding: '1rem' }}>⚠️ No pose data available</div>;

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        borderRadius: 8,
        border: '2px solid #00FF00',
        marginTop: 12,
        background: '#000',
        aspectRatio: '640 / 480',
        objectFit: 'contain'
      }}
    />
  );
};

export default PoseVisualization;